<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  // ============================================================
  // TradingCanvasBackground - canvas animado para login.
  //
  // Renderiza un fondo financiero con:
  //   - grid sutil
  //   - candlesticks animados (random walk fake-data)
  //   - linea de precio actual
  //   - particulas drift
  //   - mouse parallax (opt-in via prop interactive)
  //
  // No usa Plotly ni libs externas. Canvas API puro + requestAnimationFrame.
  // Honra prefers-reduced-motion: si el usuario lo pidio, dibuja un frame
  // estatico y no anima. Cleanup completo en onDestroy.
  // ============================================================

  type Density = 'low' | 'medium' | 'high';
  type Speed = 'slow' | 'normal' | 'fast';

  interface Props {
    density?: Density;
    speed?: Speed;
    interactive?: boolean;
    /** Clases extra para el canvas (ej. opacity reducida) */
    class?: string;
  }

  const {
    density = 'medium',
    speed = 'normal',
    interactive = false,
    class: className = '',
  }: Props = $props();

  // ---------- presets ----------
  const DENSITY_PRESETS = {
    low: { maxCandles: 50, particles: 0 },
    medium: { maxCandles: 80, particles: 30 },
    high: { maxCandles: 120, particles: 60 },
  } as const;

  const SPEED_PRESETS = {
    slow: { msPerCandle: 1500 },
    normal: { msPerCandle: 800 },
    fast: { msPerCandle: 400 },
  } as const;

  // ---------- estado ----------
  let canvas: HTMLCanvasElement | null = $state(null);
  let rafId = 0;
  let resizeObserver: ResizeObserver | null = null;
  let resizeTimer: ReturnType<typeof setTimeout> | null = null;

  // Mouse parallax (en coords -1..1 desde el centro)
  let mouseX = 0;
  let mouseY = 0;

  // ---------- random walk + estado de mercado ----------
  interface Candle {
    open: number;
    close: number;
    high: number;
    low: number;
    bullish: boolean;
  }

  interface Particle {
    x: number;
    y: number;
    vy: number;
    alpha: number;
    size: number;
  }

  const candles: Candle[] = [];
  const particles: Particle[] = [];
  let lastCandleAt = 0;
  let lastPrice = 100;

  // PRNG con seed para que el walk sea reproducible y sin "saltos" feos.
  let prngState = 0x9e3779b9;
  function rand(): number {
    prngState = (prngState * 1664525 + 1013904223) >>> 0;
    return prngState / 0xffffffff;
  }

  function nextCandle(): Candle {
    const open = lastPrice;
    // Random walk: -1.5% .. +1.5% pero con drift suave
    const drift = (rand() - 0.5) * 0.03;
    const close = open * (1 + drift);
    // High/low con wick aleatorio
    const range = Math.abs(close - open) * (1 + rand() * 1.8) + open * 0.002;
    const high = Math.max(open, close) + range * rand();
    const low = Math.min(open, close) - range * rand();
    lastPrice = close;
    return { open, close, high, low, bullish: close >= open };
  }

  function seedCandles(n: number): void {
    candles.length = 0;
    lastPrice = 100;
    for (let i = 0; i < n; i++) candles.push(nextCandle());
  }

  function seedParticles(n: number, w: number, h: number): void {
    particles.length = 0;
    for (let i = 0; i < n; i++) {
      particles.push({
        x: rand() * w,
        y: rand() * h,
        vy: -0.05 - rand() * 0.15,
        alpha: 0.1 + rand() * 0.3,
        size: 0.5 + rand() * 1.5,
      });
    }
  }

  // ---------- pintado ----------
  function paint(ctx: CanvasRenderingContext2D, dpr: number): void {
    const w = ctx.canvas.width / dpr;
    const h = ctx.canvas.height / dpr;

    // Fondo (transparente — el div padre tiene bg-background; aca solo la grilla)
    ctx.clearRect(0, 0, w, h);

    // ---------- mouse parallax offset (sutil) ----------
    const px = interactive ? mouseX * 6 : 0;
    const py = interactive ? mouseY * 6 : 0;

    ctx.save();
    ctx.translate(px, py);

    // ---------- grid ----------
    drawGrid(ctx, w, h);

    // ---------- particulas ----------
    drawParticles(ctx, w, h);

    // ---------- candles ----------
    drawCandles(ctx, w, h);

    ctx.restore();
  }

  function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
    ctx.lineWidth = 1;
    const stepY = 60;
    const stepX = 80;
    ctx.beginPath();
    for (let y = stepY; y < h; y += stepY) {
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
    }
    for (let x = stepX; x < w; x += stepX) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
    }
    ctx.stroke();
  }

  function drawParticles(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    for (const p of particles) {
      ctx.fillStyle = `rgba(216, 120, 7, ${p.alpha})`; // primary orange con alpha
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
      // mover hacia arriba
      p.y += p.vy;
      if (p.y < -5) {
        p.y = h + 5;
        p.x = rand() * w;
      }
    }
  }

  function drawCandles(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const n = candles.length;
    if (n === 0) return;

    // Calcular rango global de prices visible
    let minP = Infinity;
    let maxP = -Infinity;
    for (const c of candles) {
      if (c.low < minP) minP = c.low;
      if (c.high > maxP) maxP = c.high;
    }
    const padding = (maxP - minP) * 0.08;
    minP -= padding;
    maxP += padding;
    const rangeP = maxP - minP || 1;

    // Layout horizontal
    const slotW = w / n;
    const candleW = Math.max(2, Math.min(slotW * 0.55, 12));

    function priceToY(price: number): number {
      const normalized = (price - minP) / rangeP;
      // Centrar verticalmente con margen del 25% superior/inferior
      return h * 0.75 - normalized * (h * 0.5);
    }

    for (let i = 0; i < n; i++) {
      const c = candles[i]!;
      const cx = (i + 0.5) * slotW;
      const yOpen = priceToY(c.open);
      const yClose = priceToY(c.close);
      const yHigh = priceToY(c.high);
      const yLow = priceToY(c.low);

      // Bullish: verde discreto / Bearish: rojo discreto
      const baseColor = c.bullish
        ? 'rgba(63, 185, 80, '
        : 'rgba(248, 81, 73, ';
      // Las velas mas recientes mas saturadas; las viejas se desvanecen
      const ageFactor = i / n; // 0..1
      const alpha = 0.25 + ageFactor * 0.6;

      // Glow naranja sutil solo en algunas velas (cada 12)
      if (i % 12 === 0 && i > n - 30) {
        ctx.fillStyle = `rgba(216, 120, 7, ${0.05 * ageFactor})`;
        ctx.beginPath();
        ctx.arc(cx, (yOpen + yClose) / 2, candleW * 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Wick
      ctx.strokeStyle = `${baseColor}${alpha * 0.7})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx, yHigh);
      ctx.lineTo(cx, yLow);
      ctx.stroke();

      // Body
      ctx.fillStyle = `${baseColor}${alpha})`;
      const bodyY = Math.min(yOpen, yClose);
      const bodyH = Math.max(1, Math.abs(yClose - yOpen));
      ctx.fillRect(cx - candleW / 2, bodyY, candleW, bodyH);
    }

    // Linea de precio actual (last close) — punteada horizontal
    const lastY = priceToY(candles[n - 1]!.close);
    ctx.strokeStyle = 'rgba(216, 120, 7, 0.4)';
    ctx.setLineDash([4, 6]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, lastY);
    ctx.lineTo(w, lastY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Dot en el extremo
    ctx.fillStyle = 'rgba(216, 120, 7, 0.9)';
    ctx.beginPath();
    ctx.arc(w - 6, lastY, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  // ---------- loop ----------
  function tick(now: number): void {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    // Push nueva candle si toca
    const { msPerCandle } = SPEED_PRESETS[speed];
    if (now - lastCandleAt >= msPerCandle) {
      candles.push(nextCandle());
      const { maxCandles } = DENSITY_PRESETS[density];
      while (candles.length > maxCandles) candles.shift();
      lastCandleAt = now;
    }

    paint(ctx, dpr);
    rafId = requestAnimationFrame(tick);
  }

  function paintStaticFrame(): void {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    paint(ctx, dpr);
  }

  // ---------- size ----------
  function resizeCanvas(): void {
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    seedParticles(DENSITY_PRESETS[density].particles, rect.width, rect.height);
    paintStaticFrame();
  }

  function debouncedResize(): void {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resizeCanvas, 100);
  }

  // ---------- mouse ----------
  function handleMouseMove(e: MouseEvent): void {
    if (!interactive) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    mouseX = (e.clientX / w) * 2 - 1; // -1..1
    mouseY = (e.clientY / h) * 2 - 1;
  }

  // ---------- lifecycle ----------
  onMount(() => {
    if (!canvas) return;

    seedCandles(DENSITY_PRESETS[density].maxCandles);
    resizeCanvas();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      paintStaticFrame();
      return;
    }

    if (interactive) window.addEventListener('mousemove', handleMouseMove, { passive: true });

    if (typeof ResizeObserver !== 'undefined' && canvas) {
      resizeObserver = new ResizeObserver(debouncedResize);
      resizeObserver.observe(canvas);
    } else {
      window.addEventListener('resize', debouncedResize);
    }

    rafId = requestAnimationFrame(tick);
  });

  onDestroy(() => {
    if (rafId) cancelAnimationFrame(rafId);
    if (resizeTimer) clearTimeout(resizeTimer);
    if (resizeObserver) resizeObserver.disconnect();
    window.removeEventListener('resize', debouncedResize);
    window.removeEventListener('mousemove', handleMouseMove);
  });
</script>

<canvas
  bind:this={canvas}
  class={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
  aria-hidden="true"
></canvas>
